import axios from "axios";
import { jwtDecode } from "jwt-decode";

const baseURL = process.env.REACT_APP_API_BASE_URL;
const taskAgentBasePath = "/produto/gerenciador-tarefas";

function getToken() {
  return (
    localStorage.getItem("jwtToken") ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    ""
  );
}

export function getTaskAgentAuthContext() {
  const rawToken = getToken();
  const token = rawToken.startsWith("Bearer ") ? rawToken.slice(7) : rawToken;

  if (!token) {
    throw new Error("Token de autenticacao nao encontrado.");
  }

  const decodedToken = jwtDecode(token);
  const idClienteAipim = decodedToken?.id_cliente ?? decodedToken?.idClienteAipim ?? null;

  if (!idClienteAipim) {
    throw new Error("idClienteAipim nao encontrado no token.");
  }

  return { token, idClienteAipim, baseURL };
}

async function request(method, path, { params, data } = {}) {
  const { token, idClienteAipim } = getTaskAgentAuthContext();
  const normalizedMethod = String(method).toLowerCase();
  const shouldUseQueryId = normalizedMethod === "get" || normalizedMethod === "delete";
  const shouldUseBodyId = normalizedMethod === "post" || normalizedMethod === "put" || normalizedMethod === "patch";

  const mergedParams = shouldUseQueryId
    ? {
        ...(params || {}),
        idClienteAipim,
      }
    : params;

  const finalData =
    shouldUseBodyId && data && typeof data === "object" && !Array.isArray(data)
      ? { idClienteAipim, ...data }
      : data;

  const response = await axios({
    method,
    url: `${baseURL}${taskAgentBasePath}${path}`,
    params: mergedParams,
    data: finalData,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
}

export const taskAgentService = {
  getBootstrap() {
    return request("get", "/bootstrap");
  },

  listResponsaveis(filters = {}) {
    return request("get", "/responsaveis", { params: filters });
  },

  listPastas(filters = {}) {
    return request("get", "/pastas", { params: filters });
  },

  getPasta(idPasta) {
    return request("get", `/pastas/${idPasta}`);
  },

  createPasta(payload) {
    const normalizedPayload = {
      nomePasta: payload?.nomePasta,
      descricao: payload?.descricao || "",
      nomeWorkflow: payload?.nomeWorkflow || "",
      ativo: payload?.ativo ?? true,
    };

    if (payload?.idPastaPai != null) {
      normalizedPayload.idPastaPai = payload.idPastaPai;
    }

    return request("post", "/pastas", { data: normalizedPayload });
  },

  updatePasta(idPasta, payload) {
    return request("put", `/pastas/${idPasta}`, { data: payload });
  },

  deletePasta(idPasta) {
    return request("delete", `/pastas/${idPasta}`);
  },

  getWorkflow(idPasta) {
    return request("get", "/pastas/workflow", {
      params: { idPasta },
    });
  },

  renameWorkflow(idPasta, nomeWorkflow) {
    return request("patch", "/pastas/workflow", {
      data: {
        idPasta,
        nomeWorkflow: nomeWorkflow?.trim() || "",
      },
    });
  },

  createWorkflowStage(idPasta, payload) {
    return request("post", "/pastas/workflow/etapas", {
      data: {
        idPasta,
        nomeEtapa: payload?.nomeEtapa?.trim() || "",
        conclusivo: Boolean(payload?.conclusivo),
      },
    });
  },

  updateWorkflowStage(idPasta, payload) {
    return request("put", "/pastas/workflow/etapas", {
      data: {
        idPasta,
        idWorkflow: Number(payload?.idWorkflow),
        nomeEtapa: payload?.nomeEtapa?.trim() || "",
        conclusivo: Boolean(payload?.conclusivo),
        ativo: payload?.ativo !== false,
        ...(payload?.ordem != null ? { ordem: Number(payload.ordem) } : {}),
      },
    });
  },

  inactivateWorkflowStage(idPasta, idWorkflow) {
    return request("patch", "/pastas/workflow/etapas/inativar", {
      data: {
        idPasta,
        idWorkflow: Number(idWorkflow),
      },
    });
  },

  listTarefas(idPasta, filters = {}) {
    return request("get", `/pastas/${idPasta}/tarefas`, { params: filters });
  },

  getTarefa(idPasta, idTarefa) {
    return request("get", `/pastas/${idPasta}/tarefas/${idTarefa}`);
  },

  createTarefa(idPasta, payload) {
    return request("post", `/pastas/${idPasta}/tarefas`, { data: payload });
  },

  updateTarefa(idPasta, idTarefa, payload) {
    return request("put", `/pastas/${idPasta}/tarefas/${idTarefa}`, { data: payload });
  },

  moveTarefaWorkflow(idPasta, idTarefa, idWorkflow) {
    return request("patch", `/pastas/${idPasta}/tarefas/${idTarefa}/workflow`, {
      data: { idWorkflow },
    });
  },

  deleteTarefa(idPasta, idTarefa) {
    return request("delete", `/pastas/${idPasta}/tarefas/${idTarefa}`);
  },

  listAcompanhantes(idPasta, idTarefa) {
    return request("get", `/pastas/${idPasta}/tarefas/${idTarefa}/acompanhantes`);
  },

  addAcompanhante(idPasta, idTarefa, idUsuario) {
    return request("post", `/pastas/${idPasta}/tarefas/${idTarefa}/acompanhantes`, {
      data: { idUsuario },
    });
  },

  removeAcompanhante(idPasta, idTarefa, idUsuario) {
    return request("delete", `/pastas/${idPasta}/tarefas/${idTarefa}/acompanhantes/${idUsuario}`);
  },

  listComentarios(idPasta, idTarefa) {
    return request("get", `/pastas/${idPasta}/tarefas/${idTarefa}/comentarios`);
  },

  addComentario(idPasta, idTarefa, comentario) {
    return request("post", `/pastas/${idPasta}/tarefas/${idTarefa}/comentarios`, {
      data: { comentario },
    });
  },

  updateComentario(idPasta, idTarefa, idComentario, comentario) {
    return request("put", `/pastas/${idPasta}/tarefas/${idTarefa}/comentarios/${idComentario}`, {
      data: { comentario },
    });
  },

  deleteComentario(idPasta, idTarefa, idComentario) {
    return request("delete", `/pastas/${idPasta}/tarefas/${idTarefa}/comentarios/${idComentario}`);
  },
};

export default taskAgentService;
